<template>
  <div class="field no-select">
    <label
      v-if="label"
      class="mb-2 label no-select"
      :for="name"
    >
      <h3 class="p-600">
        {{ label }}
      </h3>
    </label>
    <p
      v-if="hasHelpText"
      class="help-text"
      v-html="finalHelpText"
    ></p>
    <v-select
      :id="name"
      v-model="model"
      v-positioned
      :options="resolvedOptions"
      :components="components"
      :disabled="isDisabled"
      :clearable="false"
      :loading="loading || libOptions.loading"
      :reduce="(opt) => (typeof opt.value !== 'undefined' ? opt.value : opt)"
      v-bind="libOptions"
      @wheel.native.stop
    >
      <div slot="no-options">Nenhum resultado encontrado</div>

      <template
        slot="option"
        slot-scope="option"
      >
        <span
          v-if="typeof option.isPublished === 'boolean'"
          class="published-status"
          :class="{ published: option.isPublished }"
          :title="option.isPublished ? 'Publicado' : 'Não Publicado'"
        >
        </span>
        <div
          class="option-label"
          :title="option.label"
        >
          {{ option.label || option }}
        </div>
      </template>

      <template #search="{ attributes, events }">
        <input
          class="vs__search"
          :required="!model && isRequired"
          v-bind="attributes"
          v-on="events"
        />
      </template>
    </v-select>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import commonProperties from './common-properties';

export default {
  name: 'VueSelect',
  components: {
    'v-select': vSelect,
  },
  directives: {
    positioned: {
      bind(el) {
        /* eslint-disable no-param-reassign */
        el.positionByViewport = () => {
          const position = el.getBoundingClientRect();
          const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;
          // Quando (position bottom do select + altura das opções + tarja de rodapé)
          // é maior que a viewport significa que ele cortaria, neste caso abrimos para cima.
          if (position.bottom + 165 + 25 > viewportHeight) {
            el.setAttribute('drop-up', true);
          } else {
            el.removeAttribute('drop-up');
          }
        };

        el.addEventListener('click', el.positionByViewport);
      },
      unbind(el) {
        el.removeEventListener('click', el.positionByViewport);
      },
    },
  },
  mixins: [commonProperties],
  inheritAttrs: false,
  props: {
    options: {
      type: [Array, Object, Function],
      default() {
        return [];
      },
    },
    components: {
      type: Object,
      default: () => ({}),
    },
    libOptions: {
      type: Object,
      default: () => ({}),
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      resolvedOptions: [],
      unwatch: null,
      loading: false,
    };
  },
  computed: {
    ...mapGetters(['activeInstanceThis', 'settings']),
  },
  async created() {
    this.loading = true;

    if (Array.isArray(this.options)) {
      this.unwatch = this.$watch(
        'options',
        (value) => {
          this.resolvedOptions = value;
        },
        { immediate: true },
      );
      this.loading = false;
    } else if (this.options && this.options.watch) {
      this.unwatch = this.activeInstanceThis.$watch(
        this.options.attribute,
        (newValue) => {
          this.resolvedOptions = this.options.handler(newValue);
          this.loading = this.activeInstanceThis.loading;
        },
        {
          deep: true,
        },
      );
      this.resolvedOptions = this.options.handler(
        this.activeInstanceThis[this.options.attribute],
      );
      this.loading = this.activeInstanceThis.loading;
    } else if (this.options.source === 'store') {
      this.resolvedOptions = await this.options.handler.apply(this, [
        this.$store,
      ]);
      this.loading = false;
    } else {
      this.resolvedOptions = await this.options.apply(this.activeInstanceThis, [
        this.settings,
      ]);
      this.loading = false;
    }

    return 0;
  },
  beforeDestroy() {
    if (this.unwatch) {
      this.unwatch();
    }
  },
};
</script>

<style scoped lang="scss">
h3 {
  font-weight: $lb-medium;
  font-size: $size-normal;
  line-height: 1.5;
  color: $dark;
}

::v-deep {
  .vs__dropdown-toggle {
    background-color: $lighter;
    border: 1px solid $light;
    border-radius: 4px;
    padding: 8px 12px; // 1px less than other uitypes since it adds a transparent 1px border
  }

  .vs--disabled .vs__clear,
  .vs--disabled .vs__dropdown-toggle,
  .vs--disabled .vs__open-indicator,
  .vs--disabled .vs__search,
  .vs--disabled .vs__selected {
    background-color: $lighter;
  }

  .vs__search,
  .vs__search:focus,
  .vs__selected-options {
    padding-left: 0;
  }

  .vs__search::placeholder,
  .vs__search::-webkit-input-placeholder {
    color: $light;
    font-size: $size-small;
  }

  .vs__selected-options {
    max-width: calc(100% - 23px); // minus vs__actions width
    flex-wrap: nowrap;
  }

  .vs__search {
    margin-top: 0;
    line-height: 1.5;
  }

  .vs__selected {
    display: block;
    margin: 0;
    padding: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
  }

  .v-select {
    &.vs--open {
      .vs__dropdown-toggle {
        border-color: $crm-blue-primary;
      }
    }
  }

  .v-select:not(.vs--single) {
    .vs__selected {
      padding: 3px 8px;
      margin-right: 6px;
      border: none;
      border-radius: 2px;
      background: #e8e8e8;
      color: $lb-black;
    }

    .vs__deselect {
      margin-left: 8px;
    }

    .vs__dropdown-option--selected {
      display: none;
    }
  }

  .vs__dropdown-menu {
    max-height: 161px; // 26 * itens + 5
  }

  .vs__dropdown-option {
    padding-right: 10px;
    padding-left: 10px;
    overflow: clip;
    text-overflow: ellipsis;

    .published-status {
      display: block;
      float: right;
      background-color: $lb-light-gray;
      width: 7px;
      height: 7px;
      margin-top: 7px;
      border-radius: 50%;

      &.published {
        background-color: $success;
      }
    }

    .published-status + .option-label {
      max-width: 96%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .vs__dropdown-option--highlight {
    background: $crm-blue-primary;
  }

  .v-select[drop-up] {
    .vs__dropdown-menu {
      top: auto;
      bottom: calc(100% - 2px);
      border-radius: 4px 4px 0 0;
      border-top-style: solid;
      border-bottom-style: none;
      box-shadow: 0 -3px 6px rgb(0 0 0 / 15%);
    }
  }

  .vs__actions {
    padding-top: 2px;
  }

  .vs__spinner {
    font-size: 4px;
  }
}
</style>
